/**
 * Curated trip tips keyed by common destination names (city or country).
 * Planning vs packing split: logistics (transit, tickets, etiquette) vs things to pack.
 * Planning suggestions: Japan has special rules (JR Pass heuristics); other regions use
 * keyword rules; any remaining destination with a place string gets generic planning tips.
 * Keywords match on whole words / phrases in each destination fragment (e.g. `oman` matches
 * “Oman” but not “woman”); multi-word keys like “hong kong” match consecutive tokens.
 */

import type { Trip } from '@/services/trips';
import type { ChecklistItem } from '@/services/checklists';

export type CityContextPackingSuggestion = { title: string; notes?: string };

export type CityContextPlanningSuggestion = {
  title: string;
  notes?: string;
  category?: ChecklistItem['category'];
};

const MAX_PACKING_SUGGESTIONS = 6;
const MAX_PLANNING_SUGGESTIONS = 6;

function normalizeToken(s: string): string {
  return s.trim().toLowerCase();
}

/** Raw destination fragments (destination line, each city/country, else trip name). */
function buildTripSegments(trip: Trip): string[] {
  const parts: string[] = [];
  if (trip.destination) parts.push(trip.destination);
  if (trip.destinations?.length) {
    for (const d of trip.destinations) {
      if (d.city) parts.push(d.city);
      if (d.country) parts.push(d.country);
    }
  }
  if (parts.length === 0 && trip.name) parts.push(trip.name);
  return parts;
}

function buildHaystack(trip: Trip): string {
  return normalizeToken(buildTripSegments(trip).join(' | '));
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Word-like tokens from a segment (lowercase a–z / 0–9 runs; periods become spaces first). */
function segmentWordTokens(segment: string): string[] {
  const n = normalizeToken(segment).replace(/\./g, ' ');
  return n.split(/[^a-z0-9]+/).filter((w) => w.length > 0);
}

/**
 * True if `keyword` matches `segment` as whole word(s), not as a substring inside another word
 * (e.g. `oman` matches “Oman” and “Muscat, Oman” but not “woman”).
 */
function keywordMatchesSegment(segment: string, keyword: string): boolean {
  const k = normalizeToken(keyword).replace(/\./g, ' ');
  if (!k) return false;
  const segFlat = normalizeToken(segment).replace(/\./g, ' ');
  const words = segmentWordTokens(segment);
  const kwParts = k.split(/\s+/).filter(Boolean);

  if (kwParts.length >= 2) {
    for (let i = 0; i <= words.length - kwParts.length; i++) {
      let ok = true;
      for (let j = 0; j < kwParts.length; j++) {
        if (words[i + j] !== kwParts[j]) {
          ok = false;
          break;
        }
      }
      if (ok) return true;
    }
    const between = kwParts.map(escapeRegex).join('[^a-z0-9]+');
    return new RegExp(`(^|[^a-z0-9])${between}([^a-z0-9]|$)`, 'i').test(segFlat);
  }

  const single = kwParts[0];
  if (!single) return false;
  if (words.includes(single)) return true;
  return new RegExp(`(^|[^a-z0-9])${escapeRegex(single)}([^a-z0-9]|$)`, 'i').test(segFlat);
}

function keywordMatchesTripSegments(segments: string[], keyword: string): boolean {
  return segments.some((seg) => keywordMatchesSegment(seg, keyword));
}

function hasMinimalDestinationContext(trip: Trip): boolean {
  const segs = buildTripSegments(trip);
  if (segs.length === 0) return false;
  return segs.some((s) => normalizeToken(s).replace(/\./g, ' ').trim().length >= 2);
}

/** Human label for subtitles (first concrete city, else destination string, else trip name). */
export function getCityContextPlaceLabel(trip: Trip): string | undefined {
  const fromDestinations = trip.destinations?.find((d) => d.city?.trim())?.city?.trim();
  const legacy = trip.destination?.trim();
  const name = trip.name?.trim();
  return fromDestinations || legacy || name || undefined;
}

const JAPAN_KEYWORDS = [
  'japan',
  'tokyo',
  'osaka',
  'kyoto',
  'hiroshima',
  'fukuoka',
  'sapporo',
  'nara',
  'nagoya',
  'okinawa',
  'kanazawa',
  'hakone',
  'yokohama',
  'kobe',
  'sendai',
];

function tripSegmentsIncludeJapan(segments: string[]): boolean {
  return JAPAN_KEYWORDS.some((k) => keywordMatchesTripSegments(segments, k));
}

/** True when the trip likely involves multiple Japanese stops (JR Pass / regional passes matter). */
export function tripSuggestsJapanRailPass(trip: Trip): boolean {
  const segments = buildTripSegments(trip);
  if (!tripSegmentsIncludeJapan(segments)) return false;

  const cities =
    trip.destinations
      ?.map((d) => d.city?.trim())
      .filter((c): c is string => !!c) ?? [];

  const distinctJapanPlaces = new Set(
    cities.filter((c) =>
      JAPAN_KEYWORDS.some((k) => k !== 'japan' && keywordMatchesSegment(c, k)),
    ),
  );

  if (distinctJapanPlaces.size >= 2) return true;

  if (trip.destination && /[,/&]| and | \+ |・|\/|\|/.test(trip.destination)) return true;

  return false;
}

type PackingRule = { keywords: string[]; suggestions: CityContextPackingSuggestion[] };

const PACKING_RULES: PackingRule[] = [
  {
    keywords: ['paris'],
    suggestions: [
      {
        title: 'Smart-casual outfit',
        notes: 'Many bistros and brasseries lean slightly dressier in the evening.',
      },
      {
        title: 'Compact umbrella',
        notes: 'Quick showers are common year-round.',
      },
    ],
  },
  {
    keywords: ['rome', 'roma', 'florence', 'firenze', 'venice', 'venezia', 'milan', 'milano', 'naples', 'napoli'],
    suggestions: [
      {
        title: 'Sturdy shoes for cobblestones',
        notes: 'Historic centers are uneven—leave thin soles at home.',
      },
      {
        title: 'Shoulder-season layers',
        notes: 'Churches often require covered shoulders; days can warm up fast.',
      },
    ],
  },
  {
    keywords: ['barcelona', 'madrid', 'seville', 'sevilla', 'valencia', 'lisbon', 'lisboa', 'porto'],
    suggestions: [
      {
        title: 'Light scarf or wrap',
        notes: 'Handy for sun, breeze, and visiting religious sites.',
      },
      {
        title: 'Theft-aware bag',
        notes: 'Busy tourist zones reward a zippered cross-body or hidden pouch.',
      },
    ],
  },
  {
    keywords: ['london', 'edinburgh', 'manchester', 'dublin', 'belfast'],
    suggestions: [
      {
        title: 'Packable rain layer',
        notes: 'Showers can appear with little warning—even in summer.',
      },
      {
        title: 'Layers for shifting temps',
        notes: 'Mornings and evenings cool off quickly.',
      },
    ],
  },
  {
    keywords: ['new york', 'nyc', 'manhattan', 'brooklyn', 'los angeles', 'san francisco', 'chicago', 'miami', 'boston', 'washington'],
    suggestions: [
      {
        title: 'Comfortable city walking shoes',
        notes: 'You will likely walk more than you expect between sights and transit.',
      },
      {
        title: 'Reusable water bottle',
        notes: 'Easy to refill on the go in most US cities.',
      },
    ],
  },
  {
    keywords: JAPAN_KEYWORDS,
    suggestions: [
      {
        title: 'Cash in small denominations',
        notes: 'Many smaller shops, shrines, and lockers still prefer yen; break large bills when you can.',
      },
      {
        title: 'Socks without holes',
        notes: 'You may remove shoes indoors (temples, some ryokan, traditional homes).',
      },
      {
        title: 'Easy slip-on shoes',
        notes: 'Frequent on/off at genkan and temples—avoid complicated lacing.',
      },
      {
        title: 'Small bag for trash',
        notes: 'Public trash bins are uncommon; many people carry a bag until they can sort/dispose properly.',
      },
      {
        title: 'Handkerchief or small towel',
        notes: 'Useful after washing hands where shared cloth towels or air dryers are less common.',
      },
      {
        title: 'Modest layers for temples/shrines',
        notes: 'Shoulders and knees covered reads as respectful at many religious sites.',
      },
    ],
  },
  {
    keywords: ['dubai', 'abu dhabi', 'uae'],
    suggestions: [
      {
        title: 'Light shawl or wrap',
        notes: 'Malls and indoor spaces can be heavily air-conditioned.',
      },
      {
        title: 'Modest layers for public spaces',
        notes: 'Shoulders and knees covered is appreciated in many shared areas.',
      },
    ],
  },
  {
    keywords: ['bali', 'jakarta', 'indonesia'],
    suggestions: [
      {
        title: 'Mosquito repellent',
        notes: 'Especially useful dawn/dusk and near greenery or water.',
      },
      {
        title: 'Quick-dry towel or sarong',
        notes: 'Beach hops and sudden rain are easier with something light that dries fast.',
      },
    ],
  },
  {
    keywords: ['sydney', 'melbourne', 'brisbane', 'perth', 'australia'],
    suggestions: [
      {
        title: 'High-SPF sunscreen + hat',
        notes: 'UV is strong even on cloudy days.',
      },
      {
        title: 'Swim gear',
        notes: 'Coastal cities often mean spontaneous beach or pool time.',
      },
    ],
  },
  {
    keywords: ['cairo', 'marrakech', 'marrakesh', 'morocco', 'egypt'],
    suggestions: [
      {
        title: 'Scarf or buff',
        notes: 'Sun, dust, and modesty at sites—all in one light layer.',
      },
      {
        title: 'Sturdy sandals or closed shoes',
        notes: 'Uneven medina streets and heat call for breathable, protective footwear.',
      },
    ],
  },
  {
    keywords: ['bangkok', 'phuket', 'chiang mai', 'thailand'],
    suggestions: [
      {
        title: 'Light rain shell',
        notes: 'Sudden tropical downpours are common in season.',
      },
      {
        title: 'Temple-appropriate clothing',
        notes: 'Knees and shoulders covered for many wats.',
      },
    ],
  },
  {
    keywords: ['hanoi', 'ho chi minh', 'saigon', 'da nang', 'nha trang', 'hue', 'hoi an', 'vietnam'],
    suggestions: [
      {
        title: 'Breathable layers + light rain layer',
        notes: 'Heat, humidity, and sudden showers—quick-dry fabrics help.',
      },
      {
        title: 'Insect repellent',
        notes: 'Useful in rural and riverside areas, especially evenings.',
      },
    ],
  },
  {
    keywords: ['taipei', 'taichung', 'kaohsiung', 'tainan', 'hualien', 'taiwan'],
    suggestions: [
      {
        title: 'Compact umbrella',
        notes: 'Sudden rain is common; typhoon season can be wet and windy.',
      },
      {
        title: 'Light jacket for A/C',
        notes: 'Metro, malls, and buses often run cold inside.',
      },
    ],
  },
  {
    keywords: ['manila', 'cebu', 'palawan', 'boracay', 'davao', 'philippines'],
    suggestions: [
      {
        title: 'Reef-safe sunscreen + rash guard',
        notes: 'Island hopping and strong sun—protect skin and reefs.',
      },
      {
        title: 'Dry bag for boats & beaches',
        notes: 'Keeps phone and cash safe on bangkas and rainy transfers.',
      },
    ],
  },
  {
    keywords: ['kuala lumpur', 'penang', 'langkawi', 'kot kinabalu', 'malacca', 'malaysia'],
    suggestions: [
      {
        title: 'Light rain layer',
        notes: 'Tropical downpours arrive fast year-round.',
      },
      {
        title: 'Modest layers for religious sites',
        notes: 'Mosques and some temples expect covered limbs.',
      },
    ],
  },
  {
    keywords: [
      'beijing',
      'shanghai',
      'guangzhou',
      'shenzhen',
      'chengdu',
      'hangzhou',
      'nanjing',
      'suzhou',
      'qingdao',
      'chongqing',
      'xian',
      "xi'an",
      'wuhan',
      'china',
    ],
    suggestions: [
      {
        title: 'VPN-capable device (if you rely on it)',
        notes: 'Many global apps and sites are inconsistent without preparation—plan how you will connect.',
      },
      {
        title: 'Mobile payment backup',
        notes: 'WeChat Pay / Alipay dominate; carry a card and some cash for edge cases.',
      },
    ],
  },
  {
    keywords: ['hong kong', 'kowloon', 'macau', 'macao'],
    suggestions: [
      {
        title: 'Octopus or contactless for transit',
        notes: 'Trams, ferries, and MTR are easiest with a local stored-value option.',
      },
      {
        title: 'Light layer for Arctic A/C',
        notes: 'Indoor spaces and transit can feel much colder than the street.',
      },
    ],
  },
  {
    keywords: ['reykjavik', 'iceland', 'vik', 'akureyri'],
    suggestions: [
      {
        title: 'Windproof & waterproof shell',
        notes: 'Weather shifts fast; coastal wind and spray are common.',
      },
      {
        title: 'Thermal base layers',
        notes: 'Even summer days can feel cold when the wind picks up.',
      },
    ],
  },
  {
    keywords: ['auckland', 'wellington', 'christchurch', 'queenstown', 'rotorua', 'new zealand'],
    suggestions: [
      {
        title: 'Layers + strong sun protection',
        notes: 'UV is harsh; mountain and fjord days swing from cool to hot.',
      },
      {
        title: 'Sturdy shoes for trails',
        notes: 'Even short walks can be muddy or uneven—grippy soles help.',
      },
    ],
  },
  {
    keywords: ['istanbul', 'cappadocia', 'ankara', 'antalya', 'izmir', 'bodrum', 'turkey'],
    suggestions: [
      {
        title: 'Scarf or wrap',
        notes: 'Sun, modesty at mosques, and cool evenings—one versatile layer.',
      },
      {
        title: 'Comfortable walking shoes',
        notes: 'Old quarters are hilly and uneven—save thin soles for dinner out.',
      },
    ],
  },
  {
    keywords: ['buenos aires', 'mendoza', 'bariloche', 'salta', 'ushuaia', 'patagonia', 'argentina'],
    suggestions: [
      {
        title: 'Layers for Patagonian wind',
        notes: 'Southern regions can flip from calm to gusty and cold quickly.',
      },
      {
        title: 'Cash-friendly mindset',
        notes: 'Smaller towns and taxis still lean on cash—keep smaller notes.',
      },
    ],
  },
  {
    keywords: ['rio de janeiro', 'são paulo', 'sao paulo', 'salvador', 'brasilia', 'florianopolis', 'brazil'],
    suggestions: [
      {
        title: 'Theft-aware daypack',
        notes: 'Busy beaches and centers reward a zippered bag worn in front.',
      },
      {
        title: 'Sun protection',
        notes: 'Strong equatorial sun—hat, sunscreen, and shade breaks.',
      },
    ],
  },
  {
    keywords: ['cape town', 'johannesburg', 'durban', 'pretoria', 'kruger', 'south africa'],
    suggestions: [
      {
        title: 'Neutral-colored safari-friendly clothing',
        notes: 'If game viewing, avoid bright white/blue and check lodge laundry limits.',
      },
      {
        title: 'Reusable water bottle',
        notes: 'Dry climate and long drives—hydrate often.',
      },
    ],
  },
  {
    keywords: ['costa rica', 'la fortuna', 'manuel antonio', 'tamarindo', 'monteverde', 'puerto viejo'],
    suggestions: [
      {
        title: 'Rain gear & quick-dry clothes',
        notes: 'Cloud forest and coast mean rain any month—dry fast beats soaked cotton.',
      },
      {
        title: 'Insect repellent',
        notes: 'Especially at dawn/dusk in jungle and lowland areas.',
      },
    ],
  },
  {
    keywords: ['bogota', 'medellin', 'cartagena', 'cali', 'santa marta', 'colombia'],
    suggestions: [
      {
        title: 'Layers for altitude (Bogotá)',
        notes: 'High cities cool off at night even near the equator.',
      },
      {
        title: 'Rain layer for coffee region',
        notes: 'Mountain microclimates can shower briefly most afternoons.',
      },
    ],
  },
  {
    keywords: ['kathmandu', 'pokhara', 'lukla', 'nepal'],
    suggestions: [
      {
        title: 'Broken-in hiking boots',
        notes: 'Treks and uneven streets punish new shoes.',
      },
      {
        title: 'Dust mask or buff',
        notes: 'Dry-season valley dust and motor traffic can irritate lungs.',
      },
    ],
  },
  {
    keywords: ['honolulu', 'oahu', 'maui', 'kauai', 'lanai', 'molokai', 'big island', 'hawaii'],
    suggestions: [
      {
        title: 'Reef-safe sunscreen',
        notes: 'Hawaii restricts some sunscreens—bring compliant mineral options.',
      },
      {
        title: 'Light rain jacket',
        notes: 'Windward sides and jungles get short, intense showers.',
      },
    ],
  },
  {
    keywords: ['lima', 'cusco', 'cuzco', 'machu picchu', 'arequipa', 'nazca', 'iquitos', 'huacachina', 'sacred valley'],
    suggestions: [
      {
        title: 'Layers for altitude (Cusco / Sacred Valley)',
        notes: 'Sun is strong but mornings and shade feel cold—pack for big swings.',
      },
      {
        title: 'Coca tea mindset + hydration',
        notes: 'Altitude hits some travelers—pace yourself, hydrate, and know when to seek help.',
      },
    ],
  },
  {
    keywords: ['riyadh', 'jeddah', 'dammam', 'medina', 'mecca', 'makkah', 'taif', 'al ula', 'neom', 'saudi', 'ksa'],
    suggestions: [
      {
        title: 'Loose, ankle-length layers',
        notes: 'Shorts and sleeveless tops stand out in many public spaces; lightweight long trousers and sleeved tops are the practical norm.',
      },
      {
        title: 'Optional abaya-style cover',
        notes: 'Some women visitors pack a lightweight open abaya or long tunic—it is not mandatory for every foreigner but can feel more comfortable in cities.',
      },
    ],
  },
  {
    keywords: ['doha', 'qatar'],
    suggestions: [
      {
        title: 'Modest “city” dress',
        notes: 'Shoulders and knees covered in malls, souqs, and the Corniche; save swimwear for hotel pools or signed beach areas.',
      },
      {
        title: 'Scarf + light jacket',
        notes: 'Desert heat outside, strong A/C inside—easy to peel on and off.',
      },
    ],
  },
  {
    keywords: ['oman', 'muscat', 'salalah', 'sohar', 'nizwa', 'khasab', 'duqm'],
    suggestions: [
      {
        title: 'Town vs resort dress',
        notes: 'Outside private resorts, covered shoulders and knees are expected; swimwear belongs at hotel pools or designated beaches.',
      },
      {
        title: 'Sun hat & grippy sandals',
        notes: 'Forts, wadis, and harbors mix hot stone, sand, and uneven steps—protect your feet and head.',
      },
    ],
  },
  {
    keywords: ['kuwait city', 'kuwait'],
    suggestions: [
      {
        title: 'Opaque, loose layers',
        notes: 'Norms are conservative—avoid sheer, tight, or very short clothing in malls and public areas.',
      },
      {
        title: 'Eyewear for dust',
        notes: 'Shamal winds can hurl sand; sunglasses or clear glasses help on exposed days.',
      },
    ],
  },
  {
    keywords: ['manama', 'bahrain', 'muharraq', 'riffa'],
    suggestions: [
      {
        title: 'Two wardrobes: hotel vs souq',
        notes: 'Expat-heavy districts are more relaxed; Manama souq and traditional areas reward modest, covered clothing.',
      },
      {
        title: 'Light wrap for A/C',
        notes: 'Malls and rides run cold compared to Gulf humidity outside.',
      },
    ],
  },
  {
    keywords: ['amman', 'petra', 'wadi rum', 'aqaba', 'jerash', 'madaba', 'dead sea'],
    suggestions: [
      {
        title: 'Sturdy shoes for sites & sand',
        notes: 'Petra and desert wadis are uneven—broken-in soles beat fashion sneakers.',
      },
      {
        title: 'Daypack hydration',
        notes: 'Dry air and long walks—carry water, especially at elevation and in the desert.',
      },
    ],
  },
  {
    keywords: [
      'tel aviv',
      'jerusalem',
      'eilat',
      'haifa',
      'netanya',
      'bethlehem',
      'ramallah',
      'dead sea',
      'israel',
    ],
    suggestions: [
      {
        title: 'Shoulders/knees cover for holy sites',
        notes: 'Dress codes are stricter at many religious places—pack a wrap or long pants.',
      },
      {
        title: 'Sun hat + water for desert areas',
        notes: 'Dead Sea and southern routes are hot and bright—shade and fluids matter.',
      },
    ],
  },
  {
    keywords: [
      'jamaica',
      'montego bay',
      'kingston',
      'negril',
      'nassau',
      'bahamas',
      'exuma',
      'barbados',
      'aruba',
      'curacao',
      'curaçao',
      'st lucia',
      'saint lucia',
      'antigua',
      'grenada',
      'turks and caicos',
      'providenciales',
      'grand cayman',
      'cayman',
      'punta cana',
      'santo domingo',
      'dominican republic',
      'puerto rico',
      'san juan',
      'st thomas',
      'st john',
      'st croix',
      'belize',
      'ambergris',
      'st maarten',
      'st martin',
      'trinidad',
      'tobago',
      'barbuda',
    ],
    suggestions: [
      {
        title: 'Reef-safe sunscreen + after-sun care',
        notes: 'Strong tropical sun and coral-friendly rules—mineral SPF and aloe help.',
      },
      {
        title: 'Insect repellent',
        notes: 'Dawn/dusk and after rain, sand flies and mozzies spike—especially near mangroves.',
      },
    ],
  },
  {
    keywords: ['schengen', 'europe', 'interrail', 'eurail', 'eastern europe', 'western europe'],
    suggestions: [
      {
        title: 'Universal adapter + power strip (optional)',
        notes: 'Outlet shapes vary; one adapter with a small strip can charge everything overnight.',
      },
      {
        title: 'Light sweater for trains & museums',
        notes: 'A/C on rail and galleries often runs cooler than the street.',
      },
    ],
  },
  {
    keywords: [
      'chamonix',
      'zermatt',
      'verbier',
      'st moritz',
      'st. moritz',
      'cortina',
      'dolomites',
      'aspen',
      'vail',
      'telluride',
      'park city',
      'taos',
      'banff',
      'lake louise',
      'jasper',
      'whistler',
      'blackcomb',
      'innsbruck',
      'grenoble',
      'meribel',
      'courchevel',
      'lech',
      'st anton',
      'st. anton',
      'niseko',
      'chamonix-mont-blanc',
    ],
    suggestions: [
      {
        title: 'Helmet (rent or bring)',
        notes: 'Resorts increasingly expect helmets; renting is fine if you do not want to fly with one.',
      },
      {
        title: 'High-SPF lip balm + goggles or sunglasses',
        notes: 'Alpine sun and wind chap lips fast; eye protection cuts glare at altitude.',
      },
    ],
  },
  {
    keywords: [
      'kenya',
      'tanzania',
      'nairobi',
      'mombasa',
      'arusha',
      'serengeti',
      'ngorongoro',
      'zanzibar',
      'maasai mara',
      'masai mara',
      'amboseli',
      'tsavo',
      'botswana',
      'maun',
      'okavango',
      'chobe',
      'kasane',
      'zimbabwe',
      'victoria falls',
      'zambia',
      'livingstone',
      'lusaka',
      'namibia',
      'windhoek',
      'etosha',
      'sossusvlei',
      'rwanda',
      'kigali',
      'uganda',
      'kampala',
      'bwindi',
    ],
    suggestions: [
      {
        title: 'Neutral earth-tone layers',
        notes: 'Olives, tans, and khakis blend on drives; avoid bright white or blue on safari.',
      },
      {
        title: 'Binoculars (compact)',
        notes: 'Worth the pack space for wildlife viewing between vehicle stops.',
      },
    ],
  },
];

const GENERIC_PACKING: CityContextPackingSuggestion[] = [
  {
    title: 'Comfortable walking shoes',
    notes: 'City exploration usually means more steps than a normal day at home.',
  },
  {
    title: 'Reusable water bottle',
    notes: 'Stay hydrated while you are out from morning to evening.',
  },
];

function collectPackingFromRules(segments: string[]): CityContextPackingSuggestion[] {
  const seen = new Set<string>();
  const out: CityContextPackingSuggestion[] = [];

  for (const rule of PACKING_RULES) {
    const hit = rule.keywords.some((k) => keywordMatchesTripSegments(segments, k));
    if (!hit) continue;
    for (const s of rule.suggestions) {
      const key = s.title.trim().toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(s);
      if (out.length >= MAX_PACKING_SUGGESTIONS) return out;
    }
  }

  return out;
}

export function getCityContextPackingSuggestions(trip: Trip): CityContextPackingSuggestion[] {
  if (!hasMinimalDestinationContext(trip)) return [];

  const segments = buildTripSegments(trip);
  const fromRules = collectPackingFromRules(segments);
  if (fromRules.length > 0) return fromRules;

  return GENERIC_PACKING.slice(0, MAX_PACKING_SUGGESTIONS);
}

type PlanningRule = { keywords: string[]; suggestions: CityContextPlanningSuggestion[] };

const GENERIC_PLANNING: CityContextPlanningSuggestion[] = [
  {
    title: 'Offline maps for your destination',
    notes: 'Download map data for the city or region so you can navigate if data is spotty.',
    category: 'preparation',
  },
  {
    title: 'Local transit: pass vs pay-as-you-go',
    notes: 'Compare day or multi-day passes with contactless fares for the routes you will actually use.',
    category: 'transportation',
  },
  {
    title: 'Roaming, eSIM, or local SIM',
    notes: 'Confirm you will have data on arrival—especially for maps, rides, and messaging.',
    category: 'preparation',
  },
  {
    title: 'Timed tickets for popular sights',
    notes: 'Museums, viewpoints, and landmarks often need advance reservations in peak season.',
    category: 'activities',
  },
  {
    title: 'Save emergency contacts',
    notes: 'Add local emergency numbers plus your embassy or consulate details for the country.',
    category: 'preparation',
  },
  {
    title: 'Check holidays & closures',
    notes: 'See if public holidays or weekly closing days overlap your dates so you are not caught out.',
    category: 'preparation',
  },
];

const PLANNING_RULES: PlanningRule[] = [
  {
    keywords: ['paris', 'lyon', 'marseille', 'nice', 'bordeaux', 'strasbourg', 'toulouse'],
    suggestions: [
      {
        title: 'Timed museum & landmark entries',
        notes: 'Major sights (Louvre, Orsay, Eiffel-style venues) often require booked slots.',
        category: 'activities',
      },
      {
        title: 'Metro, RER & zones',
        notes: 'Match tickets or Navigo Easy to the zones you need—airports and Versailles use wider zones.',
        category: 'transportation',
      },
      {
        title: 'Restaurant reservations',
        notes: 'Popular bistros book up; reserve or plan off-peak meals when you can.',
        category: 'activities',
      },
    ],
  },
  {
    keywords: ['rome', 'roma', 'florence', 'firenze', 'venice', 'venezia', 'milan', 'milano', 'naples', 'napoli'],
    suggestions: [
      {
        title: 'High-speed & regional trains',
        notes: 'Italo / Trenitalia fares usually climb closer to travel—book intercity legs early if possible.',
        category: 'transportation',
      },
      {
        title: 'Skip-the-line & church dress codes',
        notes: 'Busy basilicas and sites are easier with booked tickets; shoulders covered is standard.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: ['barcelona', 'madrid', 'seville', 'sevilla', 'valencia', 'lisbon', 'lisboa', 'porto'],
    suggestions: [
      {
        title: 'City transit cards',
        notes: 'Metro and bus multi-trip cards or contactless caps often beat single tickets for busy days.',
        category: 'transportation',
      },
      {
        title: 'Timed entries for major venues',
        notes: 'Sagrada Família and similar sights sell out—book a slot before you travel.',
        category: 'activities',
      },
    ],
  },
  {
    keywords: ['london', 'edinburgh', 'manchester', 'birmingham', 'glasgow', 'dublin', 'belfast', 'liverpool'],
    suggestions: [
      {
        title: 'Oyster, contactless, or Travelcard',
        notes: 'Daily caps apply with contactless in London; check rail vs Tube for airport and day trips.',
        category: 'transportation',
      },
      {
        title: 'National Rail vs local transit',
        notes: 'Separate tickets may apply for out-of-town legs—confirm before travel day.',
        category: 'transportation',
      },
    ],
  },
  {
    keywords: [
      'new york',
      'nyc',
      'manhattan',
      'brooklyn',
      'los angeles',
      'san francisco',
      'chicago',
      'miami',
      'boston',
      'washington',
      'seattle',
      'denver',
      'austin',
      'philadelphia',
      'atlanta',
    ],
    suggestions: [
      {
        title: 'City transit & toll apps',
        notes: 'Download the local transit app or add a card for subways, buses, and any road tolls.',
        category: 'transportation',
      },
      {
        title: 'Tipping & service expectations',
        notes: 'Sit-down dining, rideshare, and hotel staff commonly expect tips—plan a small cash buffer.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: ['dubai', 'abu dhabi', 'uae', 'sharjah'],
    suggestions: [
      {
        title: 'Friday hours & weekend shift',
        notes: 'Some venues adjust hours on Friday; confirm openings for must-do stops.',
        category: 'preparation',
      },
      {
        title: 'Dress codes in shared spaces',
        notes: 'Malls, mosques, and government sites may expect modest dress—check ahead.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: ['bali', 'jakarta', 'indonesia', 'yogyakarta', 'bandung'],
    suggestions: [
      {
        title: 'Grab or Gojek for rides & delivery',
        notes: 'Widely used for transport and food; install and add a payment method before you land.',
        category: 'transportation',
      },
      {
        title: 'Cash for small vendors',
        notes: 'Markets and small warungs often prefer cash—keep smaller bills when possible.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: ['sydney', 'melbourne', 'brisbane', 'perth', 'adelaide', 'australia', 'hobart', 'cairns'],
    suggestions: [
      {
        title: 'Local transit card (Opal, Myki, etc.)',
        notes: 'Each state uses its own card or contactless rules—set it up for airport-to-city trips.',
        category: 'transportation',
      },
      {
        title: 'Beach & sun safety',
        notes: 'Check surf flags and UV times; plan shade and water for hot days.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: ['cairo', 'marrakech', 'marrakesh', 'morocco', 'egypt', 'fez', 'casablanca'],
    suggestions: [
      {
        title: 'Cash and small change',
        notes: 'Medinas and taxis often expect cash; break large notes when you can.',
        category: 'preparation',
      },
      {
        title: 'Guided medina or site visits',
        notes: 'A licensed guide can reduce hassle at busy historic sites—book through reputable providers.',
        category: 'activities',
      },
    ],
  },
  {
    keywords: ['bangkok', 'phuket', 'chiang mai', 'thailand', 'krabi', 'pattaya'],
    suggestions: [
      {
        title: 'Grab for rides',
        notes: 'Reliable for airport transfers and city hops; compare with metered taxis where available.',
        category: 'transportation',
      },
      {
        title: 'Temple visit basics',
        notes: 'Cover shoulders and knees; remove shoes where signed; be mindful of Buddha imagery rules.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: ['berlin', 'munich', 'hamburg', 'cologne', 'frankfurt', 'germany', 'stuttgart', 'dresden', 'nuremberg'],
    suggestions: [
      {
        title: 'Deutsche Bahn saver fares',
        notes: 'ICE and IC tickets are cheaper weeks ahead; consider rail passes only if math works out.',
        category: 'transportation',
      },
      {
        title: 'Sunday & Monday closures',
        notes: 'Many shops close Sunday; some museums close Monday—double-check your must-sees.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: ['toronto', 'vancouver', 'montreal', 'calgary', 'ottawa', 'canada', 'quebec city', 'victoria bc'],
    suggestions: [
      {
        title: 'Local transit card (Presto, Compass, OPUS…)',
        notes: 'Airport links and city transit often need a card or stored-value—set up on day one.',
        category: 'transportation',
      },
      {
        title: 'National park passes',
        notes: 'If you are heading near parks or historic sites, check if a pass bundle saves money.',
        category: 'activities',
      },
    ],
  },
  {
    keywords: ['singapore'],
    suggestions: [
      {
        title: 'EZ-Link, SimplyGo, or contactless',
        notes: 'MRT and buses are easiest with a stored-value card or enabled contactless.',
        category: 'transportation',
      },
      {
        title: 'Local rules & fines',
        notes: 'No gum, strict transit and litter rules—quick read of dos and don’ts avoids surprises.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: ['seoul', 'busan', 'jeju', 'korea', 'incheon'],
    suggestions: [
      {
        title: 'T-money or transit card',
        notes: 'Works on subway, buses, and many taxis—pick up at the airport or station.',
        category: 'transportation',
      },
      {
        title: 'Local maps & translation',
        notes: 'Naver or Kakao maps often beat global apps; download offline packs if available.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: ['mexico city', 'cancun', 'guadalajara', 'tulum', 'playa del carmen', 'oaxaca', 'puerto vallarta', 'merida'],
    suggestions: [
      {
        title: 'Pesos for taxis & markets',
        notes: 'Cards work in many hotels; street food and colectivos are easier with cash.',
        category: 'preparation',
      },
      {
        title: 'Altitude & hydration (CDMX)',
        notes: 'Mexico City is high—ease in the first day and drink plenty of water.',
        category: 'health',
      },
    ],
  },
  {
    keywords: ['delhi', 'mumbai', 'bangalore', 'bengaluru', 'jaipur', 'goa', 'chennai', 'kolkata', 'hyderabad', 'udaipur', 'agra'],
    suggestions: [
      {
        title: 'Rideshare apps (Uber / Ola)',
        notes: 'Often simpler than haggling on arrival; confirm pickup zones at airports and stations.',
        category: 'transportation',
      },
      {
        title: 'Train bookings (IRCTC) if traveling by rail',
        notes: 'Long-distance trains can sell out—book official tickets early when possible.',
        category: 'transportation',
      },
    ],
  },
  {
    keywords: ['hanoi', 'ho chi minh', 'saigon', 'da nang', 'nha trang', 'hue', 'hoi an', 'vietnam'],
    suggestions: [
      {
        title: 'Grab for rides & food',
        notes: 'Widely used for taxis and delivery—set up the app and a payment method before you land.',
        category: 'transportation',
      },
      {
        title: 'Visa or e-visa rules',
        notes: 'Entry rules change—confirm the latest official requirement for your passport before booking.',
        category: 'visa',
      },
    ],
  },
  {
    keywords: ['taipei', 'taichung', 'kaohsiung', 'tainan', 'hualien', 'taiwan'],
    suggestions: [
      {
        title: 'EasyCard or iPASS',
        notes: 'Works on MRT, buses, trains, and many convenience-store purchases—pick up at the airport.',
        category: 'transportation',
      },
      {
        title: 'High-speed rail seats',
        notes: 'Popular Taipei–Kaohsiung legs can sell out on holidays—book early if you have fixed dates.',
        category: 'transportation',
      },
    ],
  },
  {
    keywords: ['manila', 'cebu', 'palawan', 'boracay', 'davao', 'philippines'],
    suggestions: [
      {
        title: 'Domestic flights & ferry buffers',
        notes: 'Island hops often mean tight connections—leave slack for weather and port delays.',
        category: 'transportation',
      },
      {
        title: 'Grab for airport & city rides',
        notes: 'Reliable in major cities; confirm pickup zones at NAIA and provincial airports.',
        category: 'transportation',
      },
    ],
  },
  {
    keywords: ['kuala lumpur', 'penang', 'langkawi', 'kot kinabalu', 'malacca', 'malaysia'],
    suggestions: [
      {
        title: 'Grab and Touch ’n Go eWallet',
        notes: 'Ride-hailing is common; toll highways may need a local payment method.',
        category: 'transportation',
      },
      {
        title: 'KLIA Express vs taxi',
        notes: 'Compare train vs ride-hail for your arrival time and group size.',
        category: 'transportation',
      },
    ],
  },
  {
    keywords: [
      'beijing',
      'shanghai',
      'guangzhou',
      'shenzhen',
      'chengdu',
      'hangzhou',
      'nanjing',
      'suzhou',
      'qingdao',
      'chongqing',
      'xian',
      "xi'an",
      'wuhan',
      'china',
    ],
    suggestions: [
      {
        title: 'Connectivity plan (apps & payments)',
        notes: 'Confirm how you will use maps, translation, and pay—many travelers rely on local apps.',
        category: 'preparation',
      },
      {
        title: 'High-speed rail IDs & tickets',
        notes: 'Trains often need passport-linked booking; popular dates sell out—reserve early.',
        category: 'transportation',
      },
    ],
  },
  {
    keywords: ['hong kong', 'kowloon', 'macau', 'macao'],
    suggestions: [
      {
        title: 'Octopus (HK) / Macau Pass',
        notes: 'Stored-value cards speed up ferries, buses, and small purchases.',
        category: 'transportation',
      },
      {
        title: 'Ferry & border crossing times',
        notes: 'HK–Macau links are easy but busy—book peak ferries or buses ahead when possible.',
        category: 'transportation',
      },
    ],
  },
  {
    keywords: ['reykjavik', 'iceland', 'vik', 'akureyri'],
    suggestions: [
      {
        title: 'Book rental car or tours early',
        notes: 'Peak season has limited vehicles and popular glacier/lagoon slots fill fast.',
        category: 'transportation',
      },
      {
        title: 'Check F-road and weather rules',
        notes: 'Interior routes need the right vehicle and conditions—don’t assume summer means easy driving.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: ['auckland', 'wellington', 'christchurch', 'queenstown', 'rotorua', 'new zealand'],
    suggestions: [
      {
        title: 'Biosecurity declaration',
        notes: 'NZ is strict on food, hiking gear, and soil—clean boots and declare honestly.',
        category: 'preparation',
      },
      {
        title: 'Intercity drive times',
        notes: 'Scenic roads are slower than maps suggest—build buffer between flights and activities.',
        category: 'transportation',
      },
    ],
  },
  {
    keywords: ['istanbul', 'cappadocia', 'ankara', 'antalya', 'izmir', 'bodrum', 'turkey'],
    suggestions: [
      {
        title: 'Museum Pass / timed entries',
        notes: 'Istanbul’s top sites reward a pass or pre-booked slots in high season.',
        category: 'activities',
      },
      {
        title: 'Domestic flights or night buses',
        notes: 'Cappadocia and coast are far from Istanbul—compare flight vs overnight bus time and cost.',
        category: 'transportation',
      },
    ],
  },
  {
    keywords: ['buenos aires', 'mendoza', 'bariloche', 'salta', 'ushuaia', 'patagonia', 'argentina'],
    suggestions: [
      {
        title: 'Blue dollar vs card rates',
        notes: 'Cash exchange dynamics change—read up shortly before travel and use reputable exchangers.',
        category: 'preparation',
      },
      {
        title: 'Domestic flight buffers',
        notes: 'Patagonia hops can shift with weather—avoid same-day international connections.',
        category: 'transportation',
      },
    ],
  },
  {
    keywords: ['rio de janeiro', 'são paulo', 'sao paulo', 'salvador', 'brasilia', 'florianopolis', 'brazil'],
    suggestions: [
      {
        title: 'PIX and card readiness',
        notes: 'PIX is ubiquitous for small merchants; carry a backup card and some cash.',
        category: 'preparation',
      },
      {
        title: 'Neighborhood-aware planning',
        notes: 'Research where you will walk after dark and prefer registered taxis or apps in unfamiliar areas.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: ['cape town', 'johannesburg', 'durban', 'pretoria', 'kruger', 'south africa'],
    suggestions: [
      {
        title: 'Park gates & self-drive rules',
        notes: 'If on safari, confirm gate times and whether you need a guided vehicle in reserves.',
        category: 'activities',
      },
      {
        title: 'Load shedding schedules',
        notes: 'Power cuts are planned in many areas—charge devices and note hotel backup power.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: ['costa rica', 'la fortuna', 'manuel antonio', 'tamarindo', 'monteverde', 'puerto viejo'],
    suggestions: [
      {
        title: '4x4 rental for some regions',
        notes: 'Mountain and Pacific access roads may require SUV clearance—check route before booking a small car.',
        category: 'transportation',
      },
      {
        title: 'National park permits',
        notes: 'Popular parks cap entries—book timed slots when required.',
        category: 'activities',
      },
    ],
  },
  {
    keywords: ['bogota', 'medellin', 'cartagena', 'cali', 'santa marta', 'colombia'],
    suggestions: [
      {
        title: 'Domestic flights vs mountain buses',
        notes: 'Andean roads are scenic but slow—flights save time between regions.',
        category: 'transportation',
      },
      {
        title: 'Altitude acclimation (Bogotá)',
        notes: 'Take it easy day one; hydrate and watch alcohol intake at elevation.',
        category: 'health',
      },
    ],
  },
  {
    keywords: ['kathmandu', 'pokhara', 'lukla', 'nepal'],
    suggestions: [
      {
        title: 'TIMS & permits for treks',
        notes: 'Most trails need permits and sometimes a guide—confirm rules for your exact route.',
        category: 'preparation',
      },
      {
        title: 'Lukla flight buffers',
        notes: 'Mountain weather cancels flights—don’t book tight international connections the next day.',
        category: 'transportation',
      },
    ],
  },
  {
    keywords: ['honolulu', 'oahu', 'maui', 'kauai', 'lanai', 'molokai', 'big island', 'hawaii'],
    suggestions: [
      {
        title: 'Inter-island flight bags',
        notes: 'Hawaiian carriers often charge for checked bags—pack to carry on if that matters.',
        category: 'transportation',
      },
      {
        title: 'Sunrise / summit reservations',
        notes: 'Haleakalā and some trails need advance booking—check as soon as dates are firm.',
        category: 'activities',
      },
    ],
  },
  {
    keywords: ['lima', 'cusco', 'cuzco', 'machu picchu', 'arequipa', 'nazca', 'iquitos', 'huacachina', 'sacred valley'],
    suggestions: [
      {
        title: 'Machu Picchu & Inca Trail permits',
        notes: 'Entry and treks are capped—book official tickets or tours as soon as dates are fixed.',
        category: 'activities',
      },
      {
        title: 'Domestic flights for long legs',
        notes: 'Lima–Cusco and jungle hops save days versus buses—compare baggage limits.',
        category: 'transportation',
      },
    ],
  },
  {
    keywords: ['riyadh', 'jeddah', 'dammam', 'medina', 'mecca', 'makkah', 'taif', 'al ula', 'neom', 'saudi', 'ksa'],
    suggestions: [
      {
        title: 'Saudi eVisa & entry steps',
        notes: 'Rules and required apps change—re-read the official eVisa site shortly before you fly.',
        category: 'visa',
      },
      {
        title: 'Mecca & Medina access',
        notes: 'Non-Muslims are not permitted in the central holy precincts—plan sightseeing and hotels outside those zones.',
        category: 'preparation',
      },
      {
        title: 'Friday & Ramadan hours',
        notes: 'Retail, dining, and offices often shorten Friday midday; Ramadan shifts meal and nightlife timing.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: ['doha', 'qatar'],
    suggestions: [
      {
        title: 'Visa or Hayya category',
        notes: 'Tourism channels have evolved—confirm which official visa or Hayya path matches your trip purpose.',
        category: 'visa',
      },
      {
        title: 'Friday afternoon quiet',
        notes: 'Some shops and services pause after Friday prayer; plan lunch and errands around it.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: ['oman', 'muscat', 'salalah', 'sohar', 'nizwa', 'khasab', 'duqm'],
    suggestions: [
      {
        title: 'Visitor-friendly mosques only',
        notes: 'Tour mosques at published times with modest dress; do not assume every mosque admits tourists.',
        category: 'preparation',
      },
      {
        title: 'Rental car & gravel coverage',
        notes: 'Wadi and mountain tracks can be unpaved—check insurance excludes off-pavement damage before you go.',
        category: 'transportation',
      },
    ],
  },
  {
    keywords: ['kuwait city', 'kuwait'],
    suggestions: [
      {
        title: 'Visa type & duration',
        notes: 'Tourist, business, and family visit rules differ—match your invitation or hotel booking to the right category.',
        category: 'visa',
      },
      {
        title: 'Alcohol expectations',
        notes: 'Kuwait is effectively dry for visitors—assume no public drinking; some hotels have very limited licensed options.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: ['manama', 'bahrain', 'muharraq', 'riffa'],
    suggestions: [
      {
        title: 'Causeway & peak weekends',
        notes: 'Saudi weekend traffic can pack hotels and malls Thursday–Saturday—book dining and stays early.',
        category: 'preparation',
      },
      {
        title: 'Visa vs GCC entry',
        notes: 'If you combine Bahrain with Saudi or another Gulf stop, confirm each border’s visa or e-gate rules.',
        category: 'visa',
      },
    ],
  },
  {
    keywords: ['amman', 'petra', 'wadi rum', 'aqaba', 'jerash', 'madaba', 'dead sea'],
    suggestions: [
      {
        title: 'Jordan Pass vs à la carte tickets',
        notes: 'If you are hitting Petra and multiple sites, compare bundled passes with individual entries.',
        category: 'activities',
      },
      {
        title: 'Desert camp & Petra night slots',
        notes: 'Wadi Rum camps and Petra by Night sell out in peak season—book before you arrive.',
        category: 'activities',
      },
    ],
  },
  {
    keywords: [
      'tel aviv',
      'jerusalem',
      'eilat',
      'haifa',
      'netanya',
      'bethlehem',
      'ramallah',
      'dead sea',
      'israel',
    ],
    suggestions: [
      {
        title: 'Weekend transit & Shabbat',
        notes: 'Buses and trains can change Friday–Saturday; plan airport links and tours around local schedules.',
        category: 'transportation',
      },
      {
        title: 'Crossing wait times',
        notes: 'If you move between the West Bank and Israel proper, build buffer for security queues.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: [
      'jamaica',
      'montego bay',
      'kingston',
      'negril',
      'nassau',
      'bahamas',
      'exuma',
      'barbados',
      'aruba',
      'curacao',
      'curaçao',
      'st lucia',
      'saint lucia',
      'antigua',
      'grenada',
      'turks and caicos',
      'providenciales',
      'grand cayman',
      'cayman',
      'punta cana',
      'santo domingo',
      'dominican republic',
      'puerto rico',
      'san juan',
      'st thomas',
      'st john',
      'st croix',
      'belize',
      'ambergris',
      'st maarten',
      'st martin',
      'trinidad',
      'tobago',
      'barbuda',
    ],
    suggestions: [
      {
        title: 'Hurricane season & travel insurance',
        notes: 'June–November peaks storm risk—check cancellation cover and flexible rebooking.',
        category: 'insurance',
      },
      {
        title: 'USD cash in small bills',
        notes: 'Many islands price in USD; taxis and beach vendors often prefer cash.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: ['schengen', 'europe', 'interrail', 'eurail', 'eastern europe', 'western europe'],
    suggestions: [
      {
        title: 'Schengen 90/180 rule',
        notes: 'If you hop multiple countries, confirm how many days you can stay visa-free in the zone.',
        category: 'visa',
      },
      {
        title: 'Train reservations & rail passes',
        notes: 'High-speed and night trains often need seat bookings even with a pass—reserve popular legs early.',
        category: 'transportation',
      },
    ],
  },
  {
    keywords: [
      'chamonix',
      'zermatt',
      'verbier',
      'st moritz',
      'st. moritz',
      'cortina',
      'dolomites',
      'aspen',
      'vail',
      'telluride',
      'park city',
      'taos',
      'banff',
      'lake louise',
      'jasper',
      'whistler',
      'blackcomb',
      'innsbruck',
      'grenoble',
      'meribel',
      'courchevel',
      'lech',
      'st anton',
      'st. anton',
      'niseko',
      'chamonix-mont-blanc',
    ],
    suggestions: [
      {
        title: 'Lift pass or multi-resort bundle',
        notes: 'Ikon, Epic, or local season bundles can beat window prices—buy online when possible.',
        category: 'activities',
      },
      {
        title: 'Avalanche & off-piste briefing',
        notes: 'If you leave marked runs, carry gear and check local bulletins—guides are worth it.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: [
      'kenya',
      'tanzania',
      'nairobi',
      'mombasa',
      'arusha',
      'serengeti',
      'ngorongoro',
      'zanzibar',
      'maasai mara',
      'masai mara',
      'amboseli',
      'tsavo',
      'botswana',
      'maun',
      'okavango',
      'chobe',
      'kasane',
      'zimbabwe',
      'victoria falls',
      'zambia',
      'livingstone',
      'lusaka',
      'namibia',
      'windhoek',
      'etosha',
      'sossusvlei',
      'rwanda',
      'kigali',
      'uganda',
      'kampala',
      'bwindi',
    ],
    suggestions: [
      {
        title: 'Yellow fever & routine boosters',
        notes: 'Some routes require proof of vaccination—check WHO-style guidance for your full itinerary.',
        category: 'health',
      },
      {
        title: 'Park fees & cash buffer',
        notes: 'Many gates and tips run smoother with small USD/EUR notes alongside local currency.',
        category: 'preparation',
      },
    ],
  },
  {
    keywords: ['amsterdam', 'rotterdam', 'utrecht', 'brussels', 'antwerp', 'zurich', 'geneva', 'basel', 'vienna', 'wien', 'prague', 'budapest', 'warsaw', 'krakow', 'stockholm', 'oslo', 'copenhagen'],
    suggestions: [
      {
        title: 'Trams, bikes, and city passes',
        notes: 'Many cities bundle transit and museum discounts—check official tourism or transit sites.',
        category: 'transportation',
      },
      {
        title: 'Museum night or timed slots',
        notes: 'Blockbuster exhibits and royal palaces often need advance tickets.',
        category: 'activities',
      },
    ],
  },
];

function collectPlanningFromRules(segments: string[]): CityContextPlanningSuggestion[] {
  const seen = new Set<string>();
  const out: CityContextPlanningSuggestion[] = [];

  for (const rule of PLANNING_RULES) {
    const hit = rule.keywords.some((k) => keywordMatchesTripSegments(segments, k));
    if (!hit) continue;
    for (const s of rule.suggestions) {
      const key = s.title.trim().toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(s);
      if (out.length >= MAX_PLANNING_SUGGESTIONS) return out;
    }
  }

  return out;
}

function japanPlanningSuggestions(trip: Trip): CityContextPlanningSuggestion[] {
  const out: CityContextPlanningSuggestion[] = [
    {
      title: 'IC transit card (Suica / PASMO / ICOCA, etc.)',
      notes: 'Add a mobile wallet IC or buy a physical card for trains, buses, and many vending machines.',
      category: 'transportation',
    },
    {
      title: 'Mobile data: eSIM or pocket WiFi',
      notes: 'Maps, transit apps, and translation work much smoother with reliable data.',
      category: 'preparation',
    },
    {
      title: 'Cultural norms: quiet trains, minimal phone use',
      notes: 'Keep voice calls rare on trains; eating while walking is uncommon in many areas.',
      category: 'preparation',
    },
    {
      title: 'Temple & shrine etiquette',
      notes: 'Follow posted rules; avoid loud groups, be mindful of photo bans and purification steps.',
      category: 'preparation',
    },
    {
      title: 'Tipping is not customary',
      notes: 'Exceptional service is shown with politeness—not extra gratuity at most places.',
      category: 'preparation',
    },
  ];

  if (tripSuggestsJapanRailPass(trip)) {
    out.splice(1, 0, {
      title: 'Japan Rail Pass or regional pass',
      notes: 'Compare JR Pass, regional passes, and single tickets—reserve seats on popular Shinkansen when needed.',
      category: 'transportation',
    });
  }

  return out;
}

export function getCityContextPlanningSuggestions(trip: Trip): CityContextPlanningSuggestion[] {
  if (!hasMinimalDestinationContext(trip)) return [];

  const segments = buildTripSegments(trip);
  if (tripSegmentsIncludeJapan(segments)) {
    return japanPlanningSuggestions(trip).slice(0, MAX_PLANNING_SUGGESTIONS);
  }

  const fromRules = collectPlanningFromRules(segments);
  if (fromRules.length > 0) {
    return fromRules.slice(0, MAX_PLANNING_SUGGESTIONS);
  }

  return GENERIC_PLANNING.slice(0, MAX_PLANNING_SUGGESTIONS);
}
